import { useMemo, useState } from 'react'
import { Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ColumnDef } from '@tanstack/react-table'
import { Button, buttonVariants } from '@/components/ui/button'
import { DataTable, EmptyMessageWithLoader } from '@/components/ui/data-table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useDiscordChannelDetails,
  useNotificationTemplates,
} from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import Card from 'app/frontend/base/components/Card'
import CopyableApiKeyValue from 'app/frontend/base/components/CopyableApiKeyValue'
import { imageUrl } from 'app/frontend/base/helpers'
import { infoByType } from 'app/frontend/base/pages/UserDashboard/subpages/UserLinkedAccounts/components/LinkedAccount'
import NotificationForm from 'app/frontend/base/pages/TeamDashboard/subpages/TeamSettings/components/NotificationForm'
import TemplateActionButtons from 'app/frontend/base/pages/TeamDashboard/subpages/TeamSettings/components/TemplateActionButtons'
import { buildNotificationTemplateColumns } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamSettings/components/notificationTemplateColumns'
import StepLayout from '../StepLayout'
import StepFooter from '../StepFooter'
import { StepProps } from '../types'

const NotificationsStep = ({ goNext, goBack, isFirst }: StepProps) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const [openTemplate, setOpenTemplate] = useState<
    Partial<wowaudit.NotificationTemplate> | undefined
  >()

  const {
    data: templates,
    error,
    isPending,
    isLoadingError,
  } = useNotificationTemplates(team.id)
  const { data: discordChannelDetails } = useDiscordChannelDetails(
    templates
      ?.filter((template) => template.targetAddress)
      ?.map((template) => template.targetAddress as string) ?? [],
  )

  const columns: ColumnDef<wowaudit.NotificationTemplate>[] = useMemo(
    () => [
      ...buildNotificationTemplateColumns({
        team,
        discordChannelDetails: discordChannelDetails ?? [],
        t,
      }),
      {
        header: '',
        id: 'actions',
        cell: ({ row }) => (
          <TemplateActionButtons
            template={row.original}
            onEdit={setOpenTemplate}
          />
        ),
      },
    ],
    [team, discordChannelDetails, t],
  )

  return (
    <StepLayout
      icon={Bell}
      title='Configure notifications'
      description='Set up templates to automatically send reminders and alerts to your team.'
    >
      <div className='mb-4 flex items-center justify-between gap-4'>
        <Button type='button' size='sm' onClick={() => setOpenTemplate({})}>
          Add template
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={templates ?? []}
        includeHeader={true}
        emptyMessage={() => (
          <EmptyMessageWithLoader
            emptyMessageKey='notifications.empty-message'
            isPending={isPending}
            isLoadingError={isLoadingError}
            error={error}
            t={t}
          />
        )}
      />

      <Card className='mt-8 flex flex-col gap-4'>
        <div className='flex items-center justify-between gap-4'>
          <h5 className='mb-0 flex items-center gap-2'>
            <img src={imageUrl(infoByType.Discord.logo)} className='w-5' />
            Install the Discord bot
          </h5>
          <a
            href='/get_discord_bot'
            target='_blank'
            rel='noopener noreferrer'
            className={buttonVariants({ size: 'sm' })}
          >
            Add to server
          </a>
        </div>

        <p className='mb-0 text-sm text-muted-foreground'>
          Discord notifications are delivered with the wowaudit bot. After
          adding it to your server, run <strong>/setup</strong> and paste the
          below API key when prompted.
        </p>

        <CopyableApiKeyValue
          value={team.privateKey}
          tooltipContent={t('dashboard.team.settings.click-to-copy')}
        />
      </Card>

      <StepFooter
        showBack={!isFirst}
        onBack={goBack}
        onContinue={goNext}
        onSkip={goNext}
      />

      <Dialog
        open={!!openTemplate}
        onOpenChange={(open) => {
          if (!open) setOpenTemplate(undefined)
        }}
      >
        <DialogContent
          className='max-w-3xl'
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {openTemplate?.id ? 'Edit notification' : 'New notification'}
            </DialogTitle>
          </DialogHeader>

          {openTemplate ? (
            <NotificationForm
              key={openTemplate.id ?? 'new'}
              template={openTemplate}
              setTemplate={setOpenTemplate}
              backLabel='Close'
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </StepLayout>
  )
}

export default NotificationsStep
